/**
 * Admin Entitlement Catalog Page
 * Read-only page showing all entitlements and subscription-to-entitlement mappings
 */

import { useMemo } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useAdminEntitlementsList } from '../../hooks/useAdminEntitlementsList';

export function EntitlementCatalogPage() {
  const {
    data: entitlementData,
    isLoading,
    error,
    refetch,
  } = useAdminEntitlementsList();

  const entitlements = entitlementData?.entitlements ?? [];
  const bundles = entitlementData?.bundles ?? [];
  const externalBundles = entitlementData?.externalBundles ?? [];

  const subscriptionEntitlementIds = useMemo(
    () => new Set(bundles.flatMap(b => b.entitlements)),
    [bundles]
  );

  const isSubscriptionGranted = (entitlementId: string) =>
    subscriptionEntitlementIds.has(entitlementId);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Entitlement Catalog
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View all entitlements and subscription bundle mappings
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-sky-600 dark:text-sky-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Entitlements
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {entitlements.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-purple-600 dark:text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Subscription-Granted
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {subscriptionEntitlementIds.size}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Subscription Bundles
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {bundles.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section A: Entitlement Catalog */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              All Entitlements
            </h2>
          </div>

          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-2">
                    Failed to load entitlements
                  </p>
                  <button
                    onClick={() => refetch()}
                    className="text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 font-medium"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : entitlements.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500 dark:text-gray-400">
                  No entitlements found
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {entitlements.map(entitlement => (
                    <tr
                      key={entitlement.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <code className="text-sm font-mono text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                          {entitlement.id}
                        </code>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {entitlement.name}
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {entitlement.description}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        {isSubscriptionGranted(entitlement.id) ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            Subscription
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            Manual only
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Section B: Subscription Bundles */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Subscription Bundles
            </h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500 dark:text-gray-400">
                Failed to load bundles
              </p>
            </div>
          ) : bundles.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500 dark:text-gray-400">
                No subscription bundles configured
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {bundles.map(bundle => (
                <div key={bundle.stripeProductId} className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {bundle.name}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      {bundle.stripeProductId}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {bundle.entitlements.map(entitlementId => {
                      const entitlement = entitlements.find(
                        e => e.id === entitlementId
                      );
                      return (
                        <span
                          key={entitlementId}
                          className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                        >
                          <code className="font-mono text-xs mr-1.5">
                            {entitlementId}
                          </code>
                          {entitlement && (
                            <span className="text-purple-500 dark:text-purple-400">
                              {entitlement.name}
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/*
          External providers. Rendered from the API's per-environment view, so
          `providerProductIds` is the exact set the grant path matches against
          HERE — the shared display names plus this environment's own configured
          ids. That distinction is the whole of #1715: the catalog carried
          Supapass display names while the live Zapier trigger sent slugs, and
          31 paying subscribers were granted nothing for two months because the
          effective set was visible in no environment.

          A product id that appears on a subscription but in no list below
          grants nothing for that provider. Since #1811 Step 3 that is true of
          the Stripe bundles above as well — they had a second route via
          product metadata until that step deleted it.
        */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              External Provider Bundles
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Non-Stripe providers, as configured for this environment. A
              product id missing from these lists grants nothing.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner size="lg" />
            </div>
          ) : externalBundles.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500 dark:text-gray-400">
                No external provider bundles configured
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {externalBundles.map(bundle => (
                <div
                  key={`${bundle.provider}:${bundle.bundleId}`}
                  className="p-6"
                >
                  <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {bundle.label}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      {bundle.provider} · {bundle.bundleId}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {bundle.entitlements.map(entitlementId => {
                      const entitlement = entitlements.find(
                        e => e.id === entitlementId
                      );
                      return (
                        <span
                          key={entitlementId}
                          className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                        >
                          <code className="font-mono text-xs mr-1.5">
                            {entitlementId}
                          </code>
                          {entitlement && (
                            <span className="text-purple-500 dark:text-purple-400">
                              {entitlement.name}
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                    Product ids that map here (
                    {bundle.providerProductIds.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {bundle.providerProductIds.map(productId => (
                      <code
                        key={productId}
                        className="font-mono text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        {productId}
                      </code>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
